import React, { useMemo } from 'react';
import {
  ReactFlow,
  MarkerType,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  Handle,
  Position,
  type Node,
  type Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Card, CardContent } from './ui/card';
import { cn } from '@/lib/utils';
import { autoLayout } from '../services/diagram.service';

// Column lineage types
export interface ColumnLineageNode {
  id: string;
  object_label: string;
  field_label: string;
  field_description?: string;
  object_type: string;
  object_id: string;
  field_id: string;
  field_name: string;
  node_type: string;
}

export interface ColumnLineageEdge {
  id: string;
  type: string;
  source: string;
  target: string;
}

export interface ColumnLineageGraph {
  nodes: ColumnLineageNode[];
  edges: ColumnLineageEdge[];
}

interface ColumnLineageGraphProps {
  graph: ColumnLineageGraph;
  className?: string;
  selectedNodeId?: string;
  selectedFieldId?: string;
}

// Single color for all nodes (as requested)
const NODE_COLOR = '#4B5563'; // Same gray as source nodes in dependency graph

// Custom node component using Card - rounded rectangles with node name on top, column name below
const CustomNode = ({ data }: { data: any }) => {
  const { object_label, field_label, active } = data;

  // Node dimensions for rounded rectangles (same as dependency graph)
  const nodeWidth = 270;
  const nodeHeight = 60;

  return (
    <Card 
      className={`rounded-lg shadow-lg relative flex flex-col cursor-pointer hover:bg-accent transition-colors ${active ? 'border-secondary border-t-4' : ''}`}
      style={{ 
        width: nodeWidth, 
        height: nodeHeight,
        borderTop: active ? undefined : `4px solid ${NODE_COLOR}`,
      }}
    >
      <Handle type="target" position={Position.Left} />
      <Handle type="source" position={Position.Right} />
      <CardContent className="p-3 flex flex-col justify-center h-full w-full">
        <span className="text-xs text-muted-foreground truncate" title={object_label}>
          {object_label}
        </span>
        <span className="text-sm font-medium leading-tight truncate" title={field_label}>
          {field_label}
        </span>
      </CardContent>
    </Card>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

// Layout algorithm: use linear auto layout from diagram service
const calculateLayout = (
  nodes: ColumnLineageNode[], 
  edges: ColumnLineageEdge[],
  selectedNodeId?: string,
  selectedFieldId?: string
) => {
  // Create basic Node and Edge structures for autoLayout
  const reactFlowNodes: Node[] = nodes.map((node) => {
    // Check if this node matches the selected node and field
    const isActive = selectedNodeId && selectedFieldId && 
                     node.object_id === selectedNodeId && 
                     node.field_id === selectedFieldId;
    
    return {
      id: node.id,
      type: 'custom',
      position: { x: 0, y: 0 }, // Will be set by autoLayout
      data: {
        object_label: node.object_label,
        field_label: node.field_label,
        field_description: node.field_description,
        object_type: node.object_type,
        object_id: node.object_id,
        field_id: node.field_id,
        field_name: node.field_name,
        node_type: node.node_type,
        active: isActive || false,
      },
    };
  });

  const reactFlowEdges: Edge[] = edges.map((edge) => ({
    id: edge.id || `${edge.source}-${edge.target}`,
    source: edge.source,
    target: edge.target,
  }));

  // Use linear auto layout with same spacing as dependency graph
  const nodePositions = autoLayout(reactFlowNodes, reactFlowEdges, 'LINEAR', 270, 60);

  // Apply positions to nodes
  const positionedNodes = reactFlowNodes.map((node) => ({
    ...node,
    position: nodePositions[node.id] || { x: 0, y: 0 },
  }));

  return positionedNodes;
};

export const ColumnLineageGraph: React.FC<ColumnLineageGraphProps> = ({
  graph,
  className,
  selectedNodeId,
  selectedFieldId,
}) => {
  // Calculate layout
  const initialNodes = useMemo(() => {
    return calculateLayout(graph.nodes, graph.edges, selectedNodeId, selectedFieldId);
  }, [graph, selectedNodeId, selectedFieldId]);

  // Convert edges to react-flow format
  const initialEdges: Edge[] = useMemo(
    () => {
      return graph.edges.map((edge) => ({
        id: edge.id || `${edge.source}-${edge.target}`,
        source: edge.source,
        target: edge.target,
        type: 'smoothstep',
        animated: false,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#64748b',
        },
        style: {
          stroke: '#64748b',
          strokeWidth: 2,
        },
      }));
    },
    [graph.edges]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes/edges when graph changes
  React.useEffect(() => {
    const newNodes = calculateLayout(graph.nodes, graph.edges, selectedNodeId, selectedFieldId);
    setNodes(newNodes);
    
    const newEdges: Edge[] = graph.edges.map((edge) => ({
      id: edge.id || `${edge.source}-${edge.target}`,
      source: edge.source,
      target: edge.target,
      type: 'smoothstep',
      animated: false,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#64748b',
      },
      style: {
        stroke: '#64748b',
        strokeWidth: 2,
      },
    }));
    setEdges(newEdges);
  }, [graph, setNodes, setEdges, selectedNodeId, selectedFieldId]);

  return (
    <div className={cn('w-full h-full', className)}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={false}
        proOptions={{hideAttribution: true}}
      >
      </ReactFlow>
    </div>
  );
};

